import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GlobalErrorService } from '@services/global-error.service';
import { catchError, Observable } from 'rxjs';
import memoizeObservable from '@app/shared/utils/memoizedObservable';
import {
  EmployeeResponse,
  HiringRequestData,
  LobResponse,
  ProjectResponse,
  SkillResponse,
  VendorResponse,
  HiringRequestResponse,
  DntProjectResponse,
} from '@interfaces/hiring-manager.model';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HiringRequestService {
  private baseUrl = environment.apiUrl;

  private getEmployeeDataMemoized = memoizeObservable(() => {
    return this.http
      .get<EmployeeResponse>(`${this.baseUrl}/hiring/employees`)
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  });

  constructor(
    private http: HttpClient,
    private errorHandler: GlobalErrorService,
  ) {}

  getLobData(): Observable<LobResponse> {
    return this.http.get<LobResponse>(`${this.baseUrl}/hiring/lob`).pipe(
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  getEmployeeData(): Observable<EmployeeResponse> {
    return this.getEmployeeDataMemoized();
  }

  getProjectData(): Observable<ProjectResponse> {
    return this.http
      .get<ProjectResponse>(`${this.baseUrl}/hiring/projects`)
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  }

  getSkillData(): Observable<SkillResponse> {
    return this.http.get<SkillResponse>(`${this.baseUrl}/hiring/skills`).pipe(
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  getVendorData(): Observable<VendorResponse> {
    return this.http.get<VendorResponse>(`${this.baseUrl}/hiring/vendors`).pipe(
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  getDntProjectData(id: number): Observable<DntProjectResponse> {
    return this.http
      .get<DntProjectResponse>(`${this.baseUrl}/hiring/lob/${id}`)
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  }

  getHiringRequestById(id: number): Observable<HiringRequestResponse> {
    return this.http
      .get<HiringRequestResponse>(`${this.baseUrl}/hiring/request/${id}`)
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  }

  createHiringRequest(
    hiringRequest: HiringRequestData,
  ): Observable<HiringRequestData> {
    return this.http
      .post<HiringRequestData>(`${this.baseUrl}/hiring/request`, hiringRequest)
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  }

  updateHiringRequest(
    hiringRequest: HiringRequestData,
  ): Observable<HiringRequestData> {
    return this.http
      .put<HiringRequestData>(`${this.baseUrl}/hiring/request`, hiringRequest)
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  }
}
